import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  FileText,
  Loader2,
  Receipt,
  Save,
  Send,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { formatCents } from '../lib/money'
import InvoiceEditor from '../components/billing/InvoiceEditor'
import ImportTimeEntriesModal from '../components/billing/ImportTimeEntriesModal'
import RegisterPaymentModal from '../components/billing/RegisterPaymentModal'
import {
  AeatRectificationCodeLabel,
  InvoiceStatusLabel,
  RectificationTypeLabel,
  type AeatRectificationCode,
  type InvoiceDetail,
  type InvoiceLineInput,
  type InvoiceSeriesDto,
  type FundsProvisionDto,
  type FundsProvisionListResponse,
  type RectificationTypeT,
} from '@toga/shared'
import { useAuth } from '../contexts/AuthContext'
import { RECORD_STATUS_LABEL, useVerifactuConfig } from '../lib/verifactu'

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const auth = useAuth()
  const canVoidPayments =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')
  const [inv, setInv] = useState<InvoiceDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [series, setSeries] = useState<InvoiceSeriesDto[]>([])
  const [provisions, setProvisions] = useState<FundsProvisionDto[]>([])
  const [lines, setLines] = useState<InvoiceLineInput[]>([])
  const [irpfRate, setIrpfRate] = useState('0')
  const [notes, setNotes] = useState('')
  const [internalNotes, setInternalNotes] = useState('')
  const [dueDate, setDueDate] = useState<string>('')
  const [fundsApplications, setFundsApplications] = useState<
    Array<{ provisionId: string; amountCents: number }>
  >([])
  const [showPayment, setShowPayment] = useState(false)
  const [showImportTime, setShowImportTime] = useState(false)
  const [showRectify, setShowRectify] = useState(false)
  const [rectifyReason, setRectifyReason] = useState('')
  const [rectifyType, setRectifyType] = useState<RectificationTypeT>('POR_DIFERENCIAS')
  const [rectifyAeat, setRectifyAeat] = useState<AeatRectificationCode>('R4')
  const vf = useVerifactuConfig()
  const vfActive = vf?.active === true
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  interface VerifactuRecordRow {
    id: string
    kind: string
    altaKind: string | null
    anulacionKind: string | null
    seq: number
    status: string
    csv: string | null
    correction: string
    aeatErrorCode: number | null
    aeatErrorDescription: string | null
    generatedAt: string
  }
  const [vfRecords, setVfRecords] = useState<VerifactuRecordRow[]>([])

  /** Anula un cobro registrado por error: se borra y queda en la auditoría con su motivo. */
  const voidPayment = async (paymentId: string, amountCents: number) => {
    if (!inv) return
    const reason = prompt(
      `Anular el cobro de ${formatCents(amountCents)}.\n\nMotivo (queda registrado):`
    )
    if (!reason || reason.trim().length < 3) return
    setError(null)
    try {
      await api.post(`/invoices/${inv.id}/payments/${paymentId}/void`, { reason: reason.trim() })
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'No se pudo anular el cobro'))
    }
  }

  const toggleAccounted = async () => {
    if (!inv) return
    setError(null)
    try {
      await api.post('/accounting/mark', { ids: [inv.id], accounted: !inv.accountedAt })
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cambiar la marca')
    }
  }

  const reload = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const { invoice } = await api.get<{ invoice: InvoiceDetail }>(`/invoices/${id}`)
      setInv(invoice)
      setLines(
        invoice.lines.map((l) => ({
          position: l.position,
          description: l.description,
          quantity: l.quantity,
          unitPriceCents: l.unitPriceCents,
          discountPct: l.discountPct,
          vatRate: l.vatRate,
          isSupplied: l.isSupplied,
          taxTreatment: l.taxTreatment ?? null,
        }))
      )
      setIrpfRate(invoice.irpfRate)
      setNotes(invoice.notes ?? '')
      setInternalNotes(invoice.internalNotes ?? '')
      setDueDate(invoice.dueDate?.slice(0, 10) ?? '')
      setFundsApplications(
        invoice.appliedProvisions.map((a) => ({
          provisionId: a.provisionId,
          amountCents: a.amountCents,
        }))
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void reload()
  }, [reload])

  useEffect(() => {
    api.get<{ items: InvoiceSeriesDto[] }>('/invoice-series').then((r) => setSeries(r.items))
  }, [])

  // Registros VERI*FACTU de la factura (estado de remisión, CSV, rechazos).
  useEffect(() => {
    if (!inv?.verifactu) {
      setVfRecords([])
      return
    }
    api
      .get<{ records: VerifactuRecordRow[] }>(`/invoices/${inv.id}/verifactu`)
      .then((r) => setVfRecords(r.records))
      .catch(() => setVfRecords([]))
  }, [inv?.id, inv?.verifactu, inv?.status])

  useEffect(() => {
    if (!inv) return
    const params = new URLSearchParams({ clientId: inv.clientId, status: 'ACTIVE' })
    api
      .get<FundsProvisionListResponse>(`/funds-provisions?${params.toString()}`)
      .then((r) => setProvisions(r.items))
      .catch(() => setProvisions([]))
  }, [inv?.clientId])

  const isDraft = inv?.status === 'BORRADOR'
  const totalFundsApplied = fundsApplications.reduce((a, b) => a + b.amountCents, 0)

  const onSaveDraft = async () => {
    if (!inv) return
    setSaving(true)
    setError(null)
    try {
      const body = {
        lines,
        irpfRate,
        notes: notes || null,
        internalNotes: internalNotes || null,
        dueDate: dueDate || null,
        fundsApplications,
      }
      await api.patch(`/invoices/${inv.id}`, body)
      await reload()
    } catch (err) {
      setError(extractErr(err))
    } finally {
      setSaving(false)
    }
  }

  const onEmit = async () => {
    if (!inv) return
    if (!confirm('Una vez emitida, la factura no se puede editar. ¿Continuar?')) return
    setSaving(true)
    setError(null)
    try {
      // Persist last changes before emitting
      await api.patch(`/invoices/${inv.id}`, {
        lines,
        irpfRate,
        notes: notes || null,
        internalNotes: internalNotes || null,
        dueDate: dueDate || null,
        fundsApplications,
      })
      await api.post(`/invoices/${inv.id}/emit`, {})
      await reload()
    } catch (err) {
      setError(extractErr(err))
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    if (!inv) return
    if (!confirm('¿Eliminar este borrador? La acción no se puede deshacer.')) return
    try {
      await api.delete(`/invoices/${inv.id}`)
      navigate('/billing')
    } catch (err) {
      setError(extractErr(err))
    }
  }

  const onRectify = async () => {
    if (!inv || rectifyReason.trim().length < 3) return
    try {
      const { invoice } = await api.post<{ invoice: InvoiceDetail }>(
        `/invoices/${inv.id}/rectify`,
        {
          reason: rectifyReason.trim(),
          type: rectifyType,
          ...(vfActive ? { aeatType: rectifyAeat } : {}),
        }
      )
      setShowRectify(false)
      setRectifyReason('')
      navigate(`/billing/${invoice.id}`)
    } catch (err) {
      setError(extractErr(err))
    }
  }

  /** Anula una factura expedida por error (solo ADMIN): genera el registro de anulación en la AEAT. */
  const onVoid = async () => {
    if (!inv) return
    const reason = prompt(
      `Anular la factura ${inv.number} expedida por error.\n\nNo es para corregir importes (para eso, «Rectificar»). ` +
        'Se comunica a la AEAT y no se puede deshacer.\n\nMotivo (queda registrado):'
    )
    if (!reason || reason.trim().length < 3) return
    setError(null)
    try {
      await api.post(`/invoices/${inv.id}/void`, { reason: reason.trim() })
      await reload()
    } catch (err) {
      setError(extractErr(err))
    }
  }

  if (loading || !inv) {
    return (
      <div className="t-muted flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Cargando factura…
      </div>
    )
  }

  const seriesName = series.find((s) => s.id === inv.seriesId)?.name ?? inv.seriesPrefix

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link
            to="/billing"
            className="text-xs t-faint hover:underline inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> Facturación
          </Link>
          <div className="flex items-baseline gap-3 mt-1 flex-wrap">
            <span className="font-mono text-sm text-toga-300">{inv.number ?? '(borrador)'}</span>
            <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
              <Receipt size={18} className="text-toga-300" />
              {inv.clientDisplayName}
            </h1>
            <span className="text-xs px-1.5 py-0.5 rounded border border-base t-secondary">
              {InvoiceStatusLabel[inv.status]}
            </span>
            {inv.ledgerEntry && (
              <Link
                to="/ctb/journal"
                title="Ver el asiento en el diario del emisor"
                className="text-xs px-1.5 py-0.5 rounded border border-toga-500/30 text-toga-300 hover:underline"
              >
                Asiento {inv.ledgerEntry.number ?? ''} · {new Date(inv.ledgerEntry.date).toLocaleDateString('es-ES', { timeZone: 'UTC' })}
              </Link>
            )}
          </div>
          <p className="text-xs t-muted mt-0.5">
            Serie {seriesName}
            {inv.caseCode && ` · ${inv.caseCode}`}
            {inv.emittedAt && ` · Emitida ${new Date(inv.emittedAt).toLocaleDateString('es-ES')}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isDraft ? (
            <>
              <button
                type="button"
                onClick={() => void onSaveDraft()}
                disabled={saving}
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50 flex items-center gap-1.5"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Guardar
              </button>
              <button
                type="button"
                onClick={() => void onEmit()}
                disabled={saving || lines.length === 0}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-1.5"
              >
                <Send size={14} /> Emitir
              </button>
              <button
                type="button"
                onClick={() => void onDelete()}
                className="w-9 h-9 grid place-items-center rounded-lg hover:bg-rose-500/10 t-muted hover:text-rose-400"
                title="Eliminar borrador"
              >
                <Trash2 size={14} />
              </button>
            </>
          ) : (
            <>
              <a
                href={`/api/invoices/${inv.id}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
              >
                <FileText size={14} /> PDF
              </a>
              {inv.status !== 'ANULADA' && (
                <button
                  type="button"
                  onClick={() => setShowPayment(true)}
                  className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
                >
                  <CheckCircle2 size={14} /> Registrar pago
                </button>
              )}
              {inv.kind === 'ORDINARIA' && (
                <button
                  type="button"
                  onClick={() => {
                    setRectifyReason('')
                    setRectifyType('POR_DIFERENCIAS')
                    setRectifyAeat('R4')
                    setShowRectify(true)
                  }}
                  className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
                >
                  Rectificar
                </button>
              )}
              {isAdmin &&
                vf?.enabled === true &&
                inv.kind === 'ORDINARIA' &&
                inv.status === 'EMITIDA' &&
                inv.paidCents === 0 &&
                inv.rectifiedByNumbers.length === 0 &&
                inv.number && (
                  <button
                    type="button"
                    onClick={() => void onVoid()}
                    title="Solo para una factura expedida por error: genera el registro de anulación"
                    className="px-3 py-1.5 rounded-lg text-sm text-rose-300 border border-rose-500/30 hover:bg-rose-500/10"
                  >
                    Anular por error
                  </button>
                )}
            </>
          )}
        </div>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {/* F21: esta factura es una rectificativa → referencia a la original */}
      {inv.kind === 'RECTIFICATIVA' && inv.rectifiesNumber && (
        <div className="surface p-3 text-xs rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200">
          Factura rectificativa
          {inv.rectificationType
            ? ` ${RectificationTypeLabel[inv.rectificationType].toLowerCase()}`
            : ''}{' '}
          de la factura <span className="font-mono">{inv.rectifiesNumber}</span>
          {inv.rectificationReason && <> · Motivo: {inv.rectificationReason}</>}
        </div>
      )}

      {inv.status === 'ANULADA' && inv.voidedAt && (
        <div className="surface p-3 text-xs rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-200">
          Anulada por error el {new Date(inv.voidedAt).toLocaleDateString('es-ES')}
          {inv.voidReason && <> · Motivo: {inv.voidReason}</>}
        </div>
      )}

      {/* F21: esta factura ha sido rectificada por otras (sigue siendo válida) */}
      {inv.rectifiedByNumbers.length > 0 && (
        <div className="surface p-3 text-xs rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200">
          Rectificada por:{' '}
          {inv.rectifiedByNumbers.map((n) => (
            <span key={n} className="font-mono mr-2">
              {n}
            </span>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-4">
        <div className="space-y-4 min-w-0">
          {/* Editor de líneas */}
          <div className="surface p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="t-primary font-semibold">Líneas</h3>
              {isDraft && (
                <button
                  type="button"
                  onClick={() => setShowImportTime(true)}
                  className="px-2.5 py-1 rounded-lg text-xs t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
                >
                  Importar tiempos…
                </button>
              )}
            </div>
            {isDraft ? (
              <InvoiceEditor
                value={lines}
                irpfRate={irpfRate}
                fundsAppliedCents={totalFundsApplied}
                onChange={setLines}
                clientId={inv.clientId}
                showTreatment={vfActive}
              />
            ) : (
              <ReadonlyLines inv={inv} />
            )}
          </div>

          {/* Notas */}
          <div className="surface p-5 space-y-3">
            <div>
              <label className="block text-xs t-muted mb-1">Notas (visibles en factura)</label>
              <textarea
                disabled={!isDraft}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary disabled:opacity-60"
              />
            </div>
            <div>
              <label className="block text-xs t-muted mb-1">Notas internas</label>
              <textarea
                disabled={!isDraft}
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                rows={2}
                className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary disabled:opacity-60"
              />
            </div>
          </div>

          {/* Pagos */}
          {inv.payments.length > 0 && (
            <div className="surface p-5">
              <h3 className="t-primary font-semibold mb-3">Pagos</h3>
              <ul className="divide-y divide-base/40 text-sm">
                {inv.payments.map((p) => (
                  <li key={p.id} className="py-2 flex items-center gap-3">
                    <span className="t-muted">
                      {new Date(p.paidAt).toLocaleDateString('es-ES')}
                    </span>
                    <span className="t-secondary text-xs">{p.method}</span>
                    {p.reference && (
                      <span className="text-xs t-faint font-mono">{p.reference}</span>
                    )}
                    <span className="ml-auto font-mono t-primary">
                      {formatCents(p.amountCents)}
                    </span>
                    {canVoidPayments && inv.status !== 'ANULADA' && (
                      <button
                        type="button"
                        onClick={() => void voidPayment(p.id, p.amountCents)}
                        title="Anular este cobro (queda registrado en la auditoría)"
                        className="text-[11px] px-2 py-1 rounded border border-base t-muted hover:text-rose-300 hover:border-rose-500/40"
                      >
                        Anular
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          {!isDraft && inv.kind !== 'PROFORMA' && (
            <div className="surface p-4 text-xs space-y-1.5">
              <h3 className="t-primary font-semibold text-sm">Gestoría</h3>
              {inv.accountedAt ? (
                <p className="text-emerald-300">
                  Pasada a la gestoría el {new Date(inv.accountedAt).toLocaleDateString('es-ES')}
                </p>
              ) : (
                <p className="t-muted">Aún no se ha pasado a la gestoría.</p>
              )}
              {canVoidPayments && (
                <button
                  type="button"
                  onClick={() => void toggleAccounted()}
                  className="mt-1 px-2.5 py-1 rounded border border-base t-secondary hover:bg-hover"
                >
                  {inv.accountedAt ? 'Quitar la marca' : 'Marcar como pasada'}
                </button>
              )}
            </div>
          )}
          {isDraft && (
            <div className="surface p-4 text-sm">
              <h3 className="t-primary font-semibold mb-2">Parámetros</h3>
              <div className="space-y-2">
                <label className="block">
                  <span className="text-xs t-muted">IRPF</span>
                  <select
                    value={irpfRate}
                    onChange={(e) => setIrpfRate(e.target.value)}
                    className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary mt-0.5"
                  >
                    <option value="0">0%</option>
                    <option value="0.07">7%</option>
                    <option value="0.15">15%</option>
                    <option value="0.19">19%</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs t-muted">Vencimiento</span>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary mt-0.5"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Provisiones disponibles */}
          {isDraft && provisions.length > 0 && (
            <div className="surface p-4 text-sm">
              <h3 className="t-primary font-semibold mb-2">Provisiones disponibles</h3>
              <ul className="space-y-2">
                {provisions.map((p) => {
                  const current = fundsApplications.find((fa) => fa.provisionId === p.id)
                  const applied = current?.amountCents ?? 0
                  return (
                    <li key={p.id} className="border border-base rounded p-2">
                      <div className="flex justify-between text-xs t-faint">
                        <span>{new Date(p.receivedAt).toLocaleDateString('es-ES')}</span>
                        <span className="font-mono">{formatCents(p.availableCents)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          max={p.availableCents / 100}
                          step="0.01"
                          value={applied ? (applied / 100).toFixed(2) : ''}
                          onChange={(e) => {
                            const cents = Math.round(Number(e.target.value || '0') * 100)
                            setFundsApplications((prev) => {
                              const others = prev.filter((fa) => fa.provisionId !== p.id)
                              return cents > 0
                                ? [...others, { provisionId: p.id, amountCents: cents }]
                                : others
                            })
                          }}
                          placeholder="0,00"
                          className="flex-1 bg-transparent border border-base rounded px-2 py-1 text-sm t-primary"
                        />
                        <span className="text-xs t-faint">€</span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          {/* Registro VERI*FACTU (solo las facturas expedidas con él) */}
          {inv.verifactu && (
            <div className="surface p-4 text-xs space-y-2">
              <h3 className="t-primary font-semibold text-sm">Registro VERI*FACTU</h3>
              {inv.verifactuQrUrl && /\/\/prewww/.test(inv.verifactuQrUrl) && (
                <p className="text-[11px] text-rose-300">
                  Entorno de pruebas de la AEAT: no es una factura comunicada a la AEAT real.
                </p>
              )}
              <div>
                <span className="t-faint">Tipo:</span>{' '}
                <span className="font-mono t-secondary">
                  {inv.aeatInvoiceType}
                  {inv.rectificationMethod ? ` · ${inv.rectificationMethod === 'S' ? 'sustitución' : 'diferencias'}` : ''}
                </span>
              </div>
              {vfRecords.length === 0 ? (
                <p className="t-faint">Sin registros.</p>
              ) : (
                <ul className="space-y-1.5">
                  {vfRecords.map((r) => (
                    <li key={r.id} className="border-t border-base/40 pt-1.5">
                      <div className="t-secondary">
                        #{r.seq} · {r.kind === 'alta' ? 'Alta' : 'Anulación'} ·{' '}
                        <span
                          className={
                            r.status === 'accepted'
                              ? 'text-emerald-300'
                              : r.status === 'rejected' || r.status === 'accepted_with_errors'
                                ? 'text-rose-300'
                                : 't-muted'
                          }
                        >
                          {RECORD_STATUS_LABEL[r.status] ?? r.status}
                        </span>
                      </div>
                      {r.csv && (
                        <div className="t-faint font-mono truncate" title={r.csv}>
                          CSV {r.csv}
                        </div>
                      )}
                      {r.aeatErrorDescription && (
                        <div className="text-rose-300/90">
                          {r.aeatErrorCode}: {r.aeatErrorDescription}
                        </div>
                      )}
                      {(r.correction === 'required' || r.correction === 'review') && (
                        <div className="text-amber-300">
                          Pendiente de subsanar{isAdmin ? ' (Ajustes → Verifactu)' : ''}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Cadena de huellas propia (NO es el registro Verifactu de la AEAT) */}
          {inv.currentHash && (
            <div className="surface p-4 text-xs space-y-1">
              <h3 className="t-primary font-semibold text-sm mb-1">Cadena de huellas propia</h3>
              <p className="t-faint text-[11px] mb-2">
                Control interno de la serie. No es el registro Verifactu de la AEAT.
              </p>
              <div>
                <span className="t-faint">Código de control:</span>{' '}
                <span className="font-mono text-toga-300">{inv.csv}</span>
              </div>
              <div className="truncate">
                <span className="t-faint">Hash:</span>{' '}
                <span className="font-mono t-secondary" title={inv.currentHash}>
                  {inv.currentHash.slice(0, 16)}…
                </span>
              </div>
              {inv.previousHash && (
                <div className="truncate">
                  <span className="t-faint">Prev:</span>{' '}
                  <span className="font-mono t-faint" title={inv.previousHash}>
                    {inv.previousHash.slice(0, 16)}…
                  </span>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      {showImportTime && (
        <ImportTimeEntriesModal
          invoiceId={inv.id}
          clientId={inv.clientId}
          onClose={() => setShowImportTime(false)}
          onImported={async () => {
            setShowImportTime(false)
            await reload()
          }}
        />
      )}

      {showPayment && (
        <RegisterPaymentModal
          invoiceId={inv.id}
          pendingCents={inv.totalCents - inv.paidCents}
          onClose={() => setShowPayment(false)}
          onSaved={async () => {
            setShowPayment(false)
            await reload()
          }}
        />
      )}

      {showRectify && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4"
          onClick={() => setShowRectify(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md surface-deep p-5 animate-slideUp space-y-4"
          >
            <div>
              <h3 className="text-base font-semibold t-primary">Emitir factura rectificativa</h3>
              <p className="text-xs t-faint mt-1">
                La factura original <span className="font-mono">{inv.number}</span> seguirá siendo
                válida; se genera un borrador rectificativo que la corrige y la referencia.
              </p>
            </div>

            <div>
              <span className="text-xs t-muted">Método de rectificación</span>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {(['POR_DIFERENCIAS', 'POR_SUSTITUCION'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setRectifyType(t)}
                    className={`px-3 py-2 rounded-lg text-sm border text-left ${
                      rectifyType === t
                        ? 'border-toga-500/60 bg-toga-500/15 t-primary'
                        : 'border-base t-secondary hover:bg-hover'
                    }`}
                  >
                    <div className="font-medium">{RectificationTypeLabel[t]}</div>
                    <div className="text-[10px] t-faint mt-0.5">
                      {t === 'POR_DIFERENCIAS'
                        ? vfActive
                          ? 'Abono total de la factura'
                          : 'Solo la diferencia (abono)'
                        : 'Importes correctos completos'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {vfActive && (
              <label className="block">
                <span className="text-xs t-muted">Causa (código de la AEAT)</span>
                <select
                  value={rectifyAeat}
                  onChange={(e) => setRectifyAeat(e.target.value as AeatRectificationCode)}
                  className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
                >
                  {(Object.keys(AeatRectificationCodeLabel) as AeatRectificationCode[]).map((c) => (
                    <option key={c} value={c}>
                      {AeatRectificationCodeLabel[c]}
                    </option>
                  ))}
                </select>
                <span className="text-[10px] t-faint">
                  Con VERI*FACTU, «por diferencias» es el abono total. Para corregir solo unos
                  importes, usa «por sustitución».
                </span>
              </label>
            )}

            <label className="block">
              <span className="text-xs t-muted">Motivo de la rectificación</span>
              <input
                value={rectifyReason}
                onChange={(e) => setRectifyReason(e.target.value)}
                placeholder="P. ej. Error en la base imponible"
                autoFocus
                className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
              />
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowRectify(false)}
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={rectifyReason.trim().length < 3}
                onClick={() => void onRectify()}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50"
              >
                Crear borrador
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ReadonlyLines({ inv }: { inv: InvoiceDetail }) {
  return (
    <table className="w-full text-sm">
      <thead className="text-xs uppercase tracking-wider t-faint border-b border-base">
        <tr>
          <th className="text-left py-1">Descripción</th>
          <th className="text-right py-1 w-16">Cant.</th>
          <th className="text-right py-1 w-24">Precio</th>
          <th className="text-right py-1 w-14">IVA</th>
          <th className="text-right py-1 w-24">Total</th>
        </tr>
      </thead>
      <tbody>
        {inv.lines.map((l) => (
          <tr key={l.id} className="border-b border-base/30">
            <td className="py-2 t-primary">{l.description}</td>
            <td className="py-2 text-right t-secondary">{l.quantity}</td>
            <td className="py-2 text-right t-secondary font-mono">
              {formatCents(l.unitPriceCents)}
            </td>
            <td className="py-2 text-right text-xs t-faint">
              {l.isSupplied ? 'Sup.' : `${(Number(l.vatRate) * 100).toFixed(0)}%`}
            </td>
            <td className="py-2 text-right t-primary font-mono">{formatCents(l.totalCents)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="text-sm">
        <tr>
          <td colSpan={4} className="py-2 text-right t-secondary">
            Base imponible
          </td>
          <td className="py-2 text-right font-mono t-primary">{formatCents(inv.subtotalCents)}</td>
        </tr>
        {inv.vatBreakdown.map((v) => (
          <tr key={v.rate}>
            <td colSpan={4} className="py-1 text-right t-secondary">
              IVA {(v.rate * 100).toFixed(0)}% s/ {formatCents(v.baseCents)}
            </td>
            <td className="py-1 text-right font-mono">{formatCents(v.vatCents)}</td>
          </tr>
        ))}
        {inv.irpfCents > 0 && (
          <tr>
            <td colSpan={4} className="py-1 text-right text-rose-300">
              IRPF ({(Number(inv.irpfRate) * 100).toFixed(2)}%)
            </td>
            <td className="py-1 text-right font-mono text-rose-300">
              -{formatCents(inv.irpfCents)}
            </td>
          </tr>
        )}
        {inv.fundsAppliedCents > 0 && (
          <tr>
            <td colSpan={4} className="py-1 text-right text-emerald-300">
              Provisión aplicada
            </td>
            <td className="py-1 text-right font-mono text-emerald-300">
              -{formatCents(inv.fundsAppliedCents)}
            </td>
          </tr>
        )}
        <tr className="border-t border-base">
          <td colSpan={4} className="pt-2 text-right t-primary font-semibold text-base">
            TOTAL
          </td>
          <td className="pt-2 text-right font-mono t-primary font-semibold text-base">
            {formatCents(inv.totalCents)}
          </td>
        </tr>
      </tfoot>
    </table>
  )
}

function extractErr(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | undefined
    return body?.error ?? err.message
  }
  return err instanceof Error ? err.message : 'Error inesperado'
}
